var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * MARC Checker (CZNA-306 / epic CZNA-292; merged with the MVL rule tester in
 * CZNA-328).
 *
 * One modal, two run modes and two input sources:
 *  - RULE SCOPE — all applicable rules (default, opened from the rules-list top
 *    button) OR one specific rule (single-rule mode, opened from a rule's detail
 *    with `ruleCode` preset). Single-rule mode threads `ruleCode` to the backend,
 *    which scopes the result to that rule across every rule kind.
 *  - RUN SOURCE — a single record (pasted as mnemonic text / MARC-in-JSON, or
 *    pulled from the DB via the record lookup) OR a bounded corpus sample of N
 *    real records of the chosen type (server-capped; corpus-wide runs have an
 *    OOM/throughput history — CZNA-179/297/318).
 *
 * Wraps `POST /czna-authorities/validation/validate-record` (single + sample
 * paths). Strictly read-only — nothing is persisted.
 */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { Modal, ModalFooter, Button, Select, Selection, MessageBanner, Loading, } from '@folio/stripes/components';
import { useValidateRecord, useValidateRecordSample } from '../../hooks/useValidateRecord';
import useRecordPickerOptions from '../../hooks/useRecordPickerOptions';
import { fetchRecord } from '../../api/recordService';
import { fetchProposalDetail } from '../../api/proposalService';
import { parseMarcText } from '../../utils/marcTextParser';
import { recordDetailToMarcText } from '../../utils/recordDetailToMarcText';
import { getErrorInfo } from '../../utils/getErrorCode';
import { recordDisplayId } from '../../utils/recordDisplayId';
import { groupByTier, } from './marcCheckerTiers';
var CHECKABLE_TYPES = [
    'PERSONAL',
    'CORPORATE',
    'MEETING',
    'UNIFORM_TITLE',
    'CHRONOLOGICAL',
    'SUBJECT',
    'GEOGRAPHIC',
    'OLD_PRINT_CITY',
    'GENRE_FORM',
];
var OPERATIONS = ['CREATE_DRAFT', 'UPDATE_DRAFT', 'PUBLISH'];
/** Module-relative base for the authority detail link (SPA navigation). */
var AUTHORITIES_BASE = '/czna-authorities/authorities';
/** Default + hard cap for the corpus sample (mirrors backend ValidationFacade). */
var DEFAULT_SAMPLE_SIZE = 10;
var MAX_SAMPLE_SIZE = 25;
var TIER_COLORS = {
    STRUCTURE: '#2b6cb0',
    VOCABULARY: '#6b46c1',
    LOGIC: '#2f855a',
};
var PLACEHOLDER = [
    'LDR 00000nz  a2200000n  4500',
    '001 ph123456',
    '100 1# $a Kundera, Milan $d 1929-2023',
    '370 ## $a Brno (Česko)',
].join('\n');
// Heading line (1XX) per authority type, used to build a runnable example.
var EXAMPLE_HEADINGS = {
    PERSONAL: '100 1# $a Kundera, Milan $d 1929-2023',
    CORPORATE: '110 2# $a Národní knihovna České republiky',
    MEETING: '111 2# $a Sjezd českých knihovníků',
    UNIFORM_TITLE: '130 0# $a Bible',
    CHRONOLOGICAL: '148 ## $a 20. století',
    SUBJECT: '150 ## $a Knihovnictví',
    GEOGRAPHIC: '151 ## $a Brno (Česko)',
    OLD_PRINT_CITY: '195 ## $a Pragae $b Česko',
    GENRE_FORM: '155 ## $a romány',
};
/** A runnable, near-valid example record for the given authority type. */
var buildExample = function (type) {
    var _a;
    return [
        'LDR 00000nz  a2200000n  4500',
        '001 ph123456',
        '003 CZ-PrNK',
        '008 200101|||aznnnaabn|||||||||||n|ana||||||',
        '040 ## $a ABA001 $b cze',
        (_a = EXAMPLE_HEADINGS[type]) !== null && _a !== void 0 ? _a : EXAMPLE_HEADINGS.PERSONAL,
        '670 ## $a Vlastní dílo',
        '678 0# $a Stručná charakteristika.',
    ].join('\n');
};
var formatOneLocation = function (loc) {
    var tag = loc === null || loc === void 0 ? void 0 : loc.tag;
    if (!tag)
        return null;
    return (loc === null || loc === void 0 ? void 0 : loc.subfieldCode) ? "".concat(tag, " $").concat(loc.subfieldCode) : tag;
};
/** All offending locations for a (deduped) issue, e.g. "100 $a, 400 $z" (CZNA-309). */
var formatLocation = function (issue) {
    var all = issue.locations && issue.locations.length > 0 ? issue.locations : [issue.location];
    var labels = all.map(formatOneLocation).filter(function (s) { return s !== null; });
    return labels.length > 0 ? Array.from(new Set(labels)).join(', ') : '—';
};
var SeverityBadge = function (_a) {
    var severity = _a.severity;
    var isBlocking = severity === 'BLOCKING';
    return (React.createElement("span", { style: {
            display: 'inline-block',
            padding: '1px 6px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#fff',
            backgroundColor: isBlocking ? '#c53030' : '#dd6b20',
            whiteSpace: 'nowrap',
        } },
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.severity.".concat(severity) })));
};
var IssueRow = function (_a) {
    var issue = _a.issue;
    return (React.createElement("li", { style: { display: 'flex', gap: '8px', alignItems: 'baseline', padding: '4px 0', borderTop: '1px solid #f0f0f0' } },
        React.createElement(SeverityBadge, { severity: issue.severity }),
        React.createElement("span", { style: { fontFamily: 'monospace', fontSize: '12px', color: '#718096', minWidth: '110px' } }, formatLocation(issue)),
        React.createElement("span", { style: { flex: 1, minWidth: 0 } },
            issue.message,
            React.createElement("span", { style: { fontFamily: 'monospace', fontSize: '11px', color: '#a0aec0', marginLeft: '6px' } },
                "(",
                issue.code,
                ")"))));
};
/** Grouped tier view of one ValidationResult (shared by single + per-sample-record). */
var TieredResult = function (_a) {
    var result = _a.result;
    var intl = useIntl();
    var tiered = groupByTier(result.errors, result.warnings);
    return (React.createElement(React.Fragment, null, tiered.map(function (_a) {
        var tier = _a.tier, errors = _a.errors, warnings = _a.warnings;
        var issues = __spreadArray(__spreadArray([], errors, true), warnings, true);
        return (React.createElement("div", { key: tier, style: { marginTop: '12px' } },
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement("span", { style: {
                        display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: TIER_COLORS[tier],
                    } }),
                React.createElement("strong", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.tier.".concat(tier) })),
                React.createElement("span", { style: { color: '#a0aec0', fontSize: '12px' } }, issues.length === 0
                    ? intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.tier.ok' })
                    : intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.tier.count' }, { errors: errors.length, warnings: warnings.length }))),
            issues.length > 0 && (React.createElement("ul", { style: { listStyle: 'none', margin: '4px 0 0', padding: 0 } }, issues.map(function (issue, i) { return (
            // eslint-disable-next-line react/no-array-index-key
            React.createElement(IssueRow, { key: "".concat(issue.code, "-").concat(i), issue: issue })); })))));
    })));
};
/**
 * One sampled record: heading link + collapsed valid/invalid summary + issues.
 *
 * <p>CZNA-977: a record with no resolved heading falls back to its identifier, and that
 * identifier is the national control number (MARC 001). `recordType` is the type the sample
 * was requested for — always a RecordType constant, never a vocabulary code — and is passed
 * in rather than assumed so the shared rule stays the only one.
 */
var SampleRecordRow = function (_a) {
    var record = _a.record, recordType = _a.recordType;
    var result = record.result;
    return (React.createElement("div", { style: { padding: '8px 0', borderTop: '1px solid #edf2f7' } },
        React.createElement("div", { style: { display: 'flex', gap: '10px', alignItems: 'center' } },
            React.createElement(Link, { to: "".concat(AUTHORITIES_BASE, "/").concat(record.recordId), style: { flex: 1, minWidth: 0 } }, record.heading || recordDisplayId({
                type: recordType,
                exportId: record.exportId,
                authorityId: record.authorityId,
            })),
            React.createElement("span", { style: { fontWeight: 700, color: result.valid ? '#2f855a' : '#c53030', fontSize: '12px' } }, result.valid
                ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.summary.valid" })
                : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.sample.recordInvalid", values: { errors: result.errorCount, warnings: result.warningCount } })))),
        !result.valid && React.createElement(TieredResult, { result: result })));
};
var MarcCheckerModal = function (_a) {
    var open = _a.open, onClose = _a.onClose, ruleCode = _a.ruleCode, ruleName = _a.ruleName, ruleId = _a.ruleId;
    var intl = useIntl();
    var ky = useOkapiKy();
    var singleRule = !!ruleCode;
    var _b = useState('text'), inputMode = _b[0], setInputMode = _b[1];
    var _c = useState('record'), runSource = _c[0], setRunSource = _c[1];
    var _d = useState(''), source = _d[0], setSource = _d[1];
    var _e = useState('PERSONAL'), recordType = _e[0], setRecordType = _e[1];
    var _f = useState('PUBLISH'), operation = _f[0], setOperation = _f[1];
    var _g = useState(DEFAULT_SAMPLE_SIZE), sampleSize = _g[0], setSampleSize = _g[1];
    var _h = useState(null), parseError = _h[0], setParseError = _h[1];
    var _j = useState(null), loadError = _j[0], setLoadError = _j[1];
    var _k = useState(false), loadingRecord = _k[0], setLoadingRecord = _k[1];
    // CZNA-340 — the authorityId of a record loaded via handleLoadRecord, so the
    // subsequent check excludes that record from the duplicate-heading search
    // (otherwise the record's own heading row "duplicates" itself). Cleared on
    // any manual edit of the textarea so a tester-introduced real duplicate is
    // still caught (see the textarea onChange below and handleClear).
    var _l = useState(undefined), loadedAuthorityId = _l[0], setLoadedAuthorityId = _l[1];
    var single = useValidateRecord();
    var sample = useValidateRecordSample({
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _b.sent();
                        setLoadError((_a = info.message) !== null && _a !== void 0 ? _a : intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.error.request' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var result = single.data;
    var sampleResult = sample.data;
    // DB record lookup — reuse the picker hook (CZNA-328: pull a real record).
    var _m = useRecordPickerOptions({ recordType: recordType }), dataOptions = _m.dataOptions, pickerLoading = _m.loading, handleFilter = _m.handleFilter, asyncFilter = _m.asyncFilter;
    // Reset internal state whenever the modal (re)opens (ui-czna-development rule:
    // a controlled modal must not show stale state on reopen).
    useEffect(function () {
        if (open) {
            setSource('');
            setParseError(null);
            setLoadError(null);
            setRunSource('record');
            setSampleSize(DEFAULT_SAMPLE_SIZE);
            setLoadedAuthorityId(undefined);
            single.reset();
            sample.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ruleCode]);
    var resetResults = function () {
        single.reset();
        sample.reset();
        setLoadError(null);
    };
    var buildRecord = function () {
        if (inputMode === 'json') {
            try {
                var parsed = JSON.parse(source);
                if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.fields)) {
                    setParseError(intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.error.jsonShape' }));
                    return null;
                }
                return parsed;
            }
            catch (_a) {
                setParseError(intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.error.jsonParse' }));
                return null;
            }
        }
        var _b = parseMarcText(source), record = _b.record, errors = _b.errors;
        if (record.fields.length === 0) {
            setParseError(intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.error.empty' }));
            return null;
        }
        setParseError(errors.length > 0 ? errors.join('\n') : null);
        return record;
    };
    var handleCheck = function () {
        setParseError(null);
        resetResults();
        if (runSource === 'sample') {
            // record omitted — backend switches to the sample path on sampleSize.
            sample.mutate({
                recordType: recordType,
                operation: operation,
                sampleSize: sampleSize,
                ruleCode: singleRule ? ruleCode : undefined,
            });
            return;
        }
        var record = buildRecord();
        if (!record)
            return;
        single.mutate({
            record: record,
            recordType: recordType,
            operation: operation,
            ruleCode: singleRule ? ruleCode : undefined,
            // CZNA-340 — only set when the textarea still holds the record exactly
            // as loaded (see handleLoadRecord / the textarea onChange below); this
            // stops the record's own heading from being flagged as a duplicate of
            // itself, while still catching a real duplicate the tester introduces
            // by editing the heading.
            excludeAuthorityId: loadedAuthorityId,
        });
    };
    var handleClear = function () {
        setSource('');
        setParseError(null);
        setLoadedAuthorityId(undefined);
        resetResults();
    };
    var handleLoadExample = function () {
        setInputMode('text');
        setRunSource('record');
        setSource(buildExample(recordType));
        setParseError(null);
        setLoadedAuthorityId(undefined);
        resetResults();
    };
    // CZNA-328: pull a real record from the DB into the textarea (tester's lookup).
    // CZNA-340: also remember its authorityId so the check excludes it as a
    // self-match (cleared as soon as the text is edited or reloaded elsewhere).
    // CZNA-351: the lookup Selection may offer a PENDING proposal (CZNA-197,
    // GEOGRAPHIC only) alongside approved records. Stripes' Selection.onChange
    // only ever passes the picked option's `value` (the id) — not the option
    // object — so which endpoint to call is decided by looking the id back up
    // in `dataOptions` and checking its `isProposal` flag, rather than always
    // calling the authority-record endpoint (a proposal id there would 404).
    // A proposal has no authorityId yet, so `loadedAuthorityId` stays unset —
    // there is nothing in the record index for it to self-match against.
    var handleLoadRecord = function (recordId) {
        if (!recordId)
            return;
        setLoadingRecord(true);
        setLoadError(null);
        setRunSource('record');
        // If `dataOptions` doesn't (yet) contain this id — e.g. a debounced
        // search re-fetch raced the click — `pickedOption` is undefined and this
        // falls through to the `fetchRecord` branch below. That reproduces the
        // pre-fix behavior (a 404 if the id turns out to be a proposal) rather
        // than throwing, so a race degrades to the old bug instead of crashing.
        var pickedOption = dataOptions.find(function (o) { return o.value === recordId; });
        var loadDetail = (pickedOption === null || pickedOption === void 0 ? void 0 : pickedOption.isProposal)
            ? fetchProposalDetail(ky, recordId).then(function (detail) {
                var _a;
                return ({
                    dataFields: (_a = detail.dataFields) !== null && _a !== void 0 ? _a : [],
                    authorityId: undefined,
                });
            })
            : fetchRecord(ky, recordId).then(function (detail) {
                var _a;
                return ({
                    dataFields: detail.dataFields,
                    authorityId: (_a = detail.authorityId) !== null && _a !== void 0 ? _a : undefined,
                });
            });
        loadDetail
            .then(function (_a) {
            var dataFields = _a.dataFields, authorityId = _a.authorityId;
            setInputMode('text');
            setSource(recordDetailToMarcText(dataFields));
            setParseError(null);
            setLoadedAuthorityId(authorityId);
            resetResults();
        })
            .catch(function () {
            setLoadError(intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.error.loadFailed' }));
        })
            .finally(function () { return setLoadingRecord(false); });
    };
    var typeOptions = CHECKABLE_TYPES.map(function (t) { return ({
        value: t,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(t) }),
    }); });
    var operationOptions = OPERATIONS.map(function (op) { return ({
        value: op,
        label: intl.formatMessage({ id: "ui-czna-authorities.marcChecker.operation.".concat(op) }),
    }); });
    var modeOptions = [
        { value: 'text', label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.mode.text' }) },
        { value: 'json', label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.mode.json' }) },
    ];
    var runSourceOptions = [
        { value: 'record', label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.runSource.record' }) },
        { value: 'sample', label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.runSource.sample' }) },
    ];
    var busy = single.isLoading || sample.isLoading || loadingRecord;
    var checkDisabled = busy
        || (runSource === 'record' && source.trim().length === 0);
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleCheck, disabled: checkDisabled, "data-testid": "marc-checker-check" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.action.check" })),
        React.createElement(Button, { onClick: handleClear, disabled: busy },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.action.clear" })),
        React.createElement(Button, { onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.action.close" }))));
    var renderSingleResult = function () {
        if (single.isLoading) {
            return React.createElement("div", { style: { padding: '24px', textAlign: 'center' } },
                React.createElement(Loading, { size: "large" }));
        }
        if (single.isError) {
            return (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.error.request" })));
        }
        if (!result)
            return null;
        return (React.createElement("div", { style: { marginTop: '12px' }, "data-testid": "marc-checker-single-result" },
            React.createElement(MessageBanner, { type: result.valid ? 'success' : 'error' }, result.valid
                ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.summary.valid" })
                : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.summary.invalid", values: { errors: result.errorCount, warnings: result.warningCount } }))),
            React.createElement(TieredResult, { result: result })));
    };
    var renderSampleResult = function () {
        if (sample.isLoading) {
            return React.createElement("div", { style: { padding: '24px', textAlign: 'center' } },
                React.createElement(Loading, { size: "large" }));
        }
        if (!sampleResult)
            return null;
        return (React.createElement("div", { style: { marginTop: '12px' }, "data-testid": "marc-checker-sample-result" },
            React.createElement(MessageBanner, { type: sampleResult.invalidCount === 0 ? 'success' : 'warning' },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.sample.summary", values: {
                        sampleSize: sampleResult.sampleSize,
                        valid: sampleResult.validCount,
                        invalid: sampleResult.invalidCount,
                    } })),
            sampleResult.records.map(function (rec) { return (React.createElement(SampleRecordRow, { key: rec.recordId, record: rec, recordType: recordType })); })));
    };
    var hasOutput = busy || single.isError
        || (runSource === 'sample' ? !!sampleResult : !!result);
    return (React.createElement(Modal, { open: open, onClose: onClose, label: singleRule
            ? intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.titleScoped' }, { name: ruleName || ruleId || ruleCode })
            : intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.title' }), dismissible: true, size: "large", footer: footer },
        React.createElement("div", { "data-testid": "marc-checker-rule-scope", style: {
                margin: '0 0 12px',
                padding: '8px 12px',
                borderRadius: '4px',
                background: singleRule ? '#0052cc' : '#f0f0f0',
                color: singleRule ? '#fff' : '#444',
                fontWeight: 500,
            } }, singleRule ? (React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.scope.singleRule", values: { name: ruleName || ruleId || ruleCode, code: ruleCode } })) : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.scope.allRules" }))),
        React.createElement("p", { style: { color: '#666', margin: '0 0 12px' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.subtitle" })),
        React.createElement("div", { style: { display: 'flex', gap: '20px', alignItems: 'stretch' } },
            React.createElement("div", { style: { flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column' } },
                React.createElement("div", { style: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' } },
                    React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.runSource' }), value: runSource, dataOptions: runSourceOptions, onChange: function (e) {
                            setRunSource(e.target.value);
                            resetResults();
                        } }),
                    React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.recordType' }), value: recordType, dataOptions: typeOptions, onChange: function (e) { return setRecordType(e.target.value); } }),
                    React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.operation' }), value: operation, dataOptions: operationOptions, onChange: function (e) { return setOperation(e.target.value); } })),
                runSource === 'sample' ? (React.createElement("div", { "data-testid": "marc-checker-sample-controls" },
                    React.createElement("label", { htmlFor: "marc-checker-sample-size", style: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '4px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.field.sampleSize" }),
                        React.createElement("input", { id: "marc-checker-sample-size", type: "number", min: 1, max: MAX_SAMPLE_SIZE, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.sampleSize' }), value: String(sampleSize), onChange: function (e) {
                                var n = Number(e.target.value);
                                if (Number.isFinite(n)) {
                                    // Clamp to [1, MAX_SAMPLE_SIZE] — corpus-wide runs are unsafe.
                                    setSampleSize(Math.max(1, Math.min(MAX_SAMPLE_SIZE, Math.trunc(n))));
                                }
                            }, style: {
                                display: 'block',
                                marginTop: '4px',
                                width: '120px',
                                boxSizing: 'border-box',
                                padding: '6px 8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '13px',
                            } })),
                    React.createElement("p", { style: { color: '#718096', fontSize: '12px', margin: '4px 0 0' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.sample.hint", values: { max: MAX_SAMPLE_SIZE } })))) : (React.createElement(React.Fragment, null,
                    React.createElement("div", { style: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' } },
                        React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.mode' }), value: inputMode, dataOptions: modeOptions, onChange: function (e) { return setInputMode(e.target.value); } }),
                        React.createElement("div", { style: { flex: 1, minWidth: '200px' } },
                            React.createElement(Selection, { id: "marc-checker-record-lookup", label: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.lookup' }), placeholder: intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.lookupPlaceholder' }), dataOptions: dataOptions, value: "", onChange: handleLoadRecord, onFilter: asyncFilter ? handleFilter : undefined, asyncFilter: asyncFilter, loading: pickerLoading }))),
                    React.createElement("div", { style: {
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px',
                        } },
                        React.createElement("span", { style: { fontSize: '13px', fontWeight: 600, color: '#444' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.field.record" })),
                        React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: handleLoadExample },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.action.loadExample" }))),
                    React.createElement("textarea", { value: source, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.marcChecker.field.record' }), placeholder: inputMode === 'text' ? PLACEHOLDER : '{ "leader": "...", "fields": [ ... ] }', onChange: function (e) {
                            setSource(e.target.value);
                            // CZNA-340: any manual edit invalidates the loaded-record
                            // exclusion — the text may no longer represent that record
                            // exactly, so a real self-introduced duplicate must still
                            // be flagged.
                            setLoadedAuthorityId(undefined);
                        }, spellCheck: false, style: {
                            flex: 1,
                            minHeight: '320px',
                            width: '100%',
                            boxSizing: 'border-box',
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            lineHeight: 1.5,
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            resize: 'vertical',
                        } }),
                    parseError && (React.createElement(MessageBanner, { type: "warning" },
                        React.createElement("span", { style: { whiteSpace: 'pre-wrap' } }, parseError))))),
                loadError && (React.createElement(MessageBanner, { type: "error", role: "alert" }, loadError))),
            React.createElement("div", { style: {
                    flex: '1 1 50%',
                    minWidth: 0,
                    borderLeft: '1px solid #e8e8e8',
                    paddingLeft: '20px',
                    maxHeight: '520px',
                    overflowY: 'auto',
                } }, hasOutput
                ? (runSource === 'sample' ? renderSampleResult() : renderSingleResult())
                : (React.createElement("div", { style: { color: '#a0aec0', fontSize: '13px', paddingTop: '8px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.marcChecker.results.empty" })))))));
};
export default MarcCheckerModal;
